import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";

// 2026 Strategy: High-resolution assets that imply "Quiet Luxury"
const blackoutImages = {
  hero: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1200&q=80",
  nursery: "https://images.unsplash.com/photo-1544124499-58912cbddaad?auto=format&fit=crop&w=600&q=80",
  cinema: "https://images.unsplash.com/photo-1593784991095-a205069470b6?auto=format&fit=crop&w=600&q=80",
};

function BlackoutBlinds() {
  return (
    <div className="max-w-7xl mx-auto px-6 py-12 text-gray-900 leading-relaxed">
      <Helmet>
        <title>Blackout Blinds UK | 100% Light Blocking & Sleep Technology</title>
        <meta
          name="description"
          content="Achieve total darkness with MF Blinds. Our 2026 blackout collection features thermal insulation, noise reduction, and smart motorization for bedrooms and cinemas."
        />
        <meta name="keywords" content="Total Blackout Blinds, Sleep Hygiene Blinds UK, Thermal Blackout Shades, Nursery Window Treatments, Home Cinema Blinds" />
      </Helmet>

      {/* --- Section 1: Hero & Sleep Science --- */}
      <header className="grid lg:grid-cols-2 gap-16 items-center mb-24">
        <div>
          <h1 className="text-5xl md:text-7xl font-black mb-6 tracking-tight text-slate-900">
            The Science of <span className="text-blue-700 underline decoration-indigo-200">Total Darkness</span>.
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            In 2026, sleep is a health priority. Even minimal light pollution can reduce <strong>Melatonin production by up to 50%</strong>. Our made-to-measure blackout blinds don't just "dim" a room—they create a light-sealed sanctuary designed to stabilize your circadian rhythm and enhance REM sleep.
          </p>
          <div className="flex flex-wrap gap-4">
            <button className="bg-slate-900 text-white px-10 py-4 rounded-full font-bold hover:bg-blue-700 transition-all shadow-xl">
              Shop 100% Blackout
            </button>
            <Link to="/measuring-guide" className="px-10 py-4 rounded-full border-2 border-slate-200 font-bold hover:bg-slate-50 transition">
              View Fitting Guide
            </Link>
          </div>
        </div>
        <div className="relative">
          <img 
            src={blackoutImages.hero} 
            alt="Deep charcoal blackout roller blind in a minimalist bedroom" 
            className="rounded-[2rem] shadow-2xl h-[500px] w-full object-cover"
          />
          <div className="absolute -top-4 -right-4 bg-white p-4 rounded-2xl shadow-lg border border-slate-100 flex items-center gap-3">
            <span className="text-2xl">🔋</span>
            <div>
              <p className="font-bold text-sm">Smart Compatible</p>
              <p className="text-xs text-gray-500">Alexa & Google Home</p>
            </div>
          </div>
        </div>
      </header>

      {/* --- Section 2: Performance Grid --- */}
      <section className="bg-slate-50 rounded-[3rem] p-12 md:p-20 mb-24">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">Beyond Light Blocking: The Triple Benefit</h2>
        <div className="grid md:grid-cols-3 gap-12">
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 transform hover:-translate-y-2 transition">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">🌡️ Thermal Control</h3>
            <p className="text-gray-600">Our multi-layer fabrics reflect solar heat in summer and trap warmth in winter, potentially reducing energy costs by up to 20%.</p>
          </div>
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 transform hover:-translate-y-2 transition">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">🔇 Acoustic Dampening</h3>
            <p className="text-gray-600">Heavy-weight blackout materials provide a natural sound barrier against traffic noise and busy UK neighborhoods.</p>
          </div>
          <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 transform hover:-translate-y-2 transition">
            <h3 className="text-xl font-bold mb-4 flex items-center gap-2">👶 Child Safe Design</h3>
            <p className="text-gray-600">All MF Blackout systems are cordless as standard or feature tensioned safety clips to meet the latest safety regulations.</p>
          </div>
        </div>
      </section>

      {/* --- Section 3: Specialized Use Cases --- */}
      <div className="space-y-24 mb-24">
        {/* Nursery Case */}
        <section className="flex flex-col md:flex-row gap-12 items-center">
          <img src={blackoutImages.nursery} alt="Baby nursery with blackout blinds" className="w-full md:w-1/2 rounded-3xl shadow-lg" />
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-4 text-indigo-900">Nurseries & Kid's Rooms</h2>
            <p className="text-lg text-gray-700 mb-6">
              Early sunrises shouldn't mean early wake-ups. Our <strong>SleepEasy™ Roller collection</strong> is a favorite for parents across the UK, ensuring afternoon naps are undisturbed and summer bedtimes are actually dark.
            </p>
            <Link to="/perfect-fit-blinds" className="text-blue-700 font-bold hover:underline">See No-Drill Options for Nurseries →</Link>
          </div>
        </section>

        {/* Media Room Case */}
        <section className="flex flex-col md:flex-row-reverse gap-12 items-center">
          <img src={blackoutImages.cinema} alt="Home cinema with blackout blinds" className="w-full md:w-1/2 rounded-3xl shadow-lg" />
          <div className="md:w-1/2">
            <h2 className="text-3xl font-bold mb-4 text-indigo-900">Home Cinemas & Media Rooms</h2>
            <p className="text-lg text-gray-700 mb-6">
              For the true cinephile, "good enough" isn't an option. Our <strong>Total Blackout Cassette systems</strong> feature side channels that eliminate the "halo effect," providing a 100% light-sealed environment for daytime gaming and movie marathons.
            </p>
          </div>
        </section>
      </div>

      {/* --- Section 4: 2026 Buying Guide (SEO Question Chain) --- */}
      <section className="max-w-4xl mx-auto py-16 border-t border-slate-100">
        <h2 className="text-3xl font-bold mb-10 text-center">Blackout Blinds Buying Guide</h2>
        <div className="space-y-8">
          <div className="group border-b border-slate-100 pb-6">
            <h4 className="text-xl font-bold mb-2">What is the difference between "Room Darkening" and "Blackout"?</h4>
            <p className="text-gray-600">Room darkening fabrics filter about 90-95% of light, while true <strong>blackout</strong> fabrics are 100% opaque. If you need complete darkness for sleep, always specify 100% blackout with a rear liner.</p>
          </div>
          <div className="group border-b border-slate-100 pb-6">
            <h4 className="text-xl font-bold mb-2">How do I prevent light leaks around the edges?</h4>
            <p className="text-gray-600">Standard blinds leave a small "halo" gap. To solve this, we recommend an <strong>Outside Mount</strong> (overlapping the window frame) or our <strong>Perfect Fit™</strong> frame system that clips flush to the glass.</p>
          </div>
          <div className="group border-b border-slate-100 pb-6">
            <h4 className="text-xl font-bold mb-2">Can blackout blinds be stylish?</h4>
            <p className="text-gray-600">Absolutely. In 2026, the trend has shifted toward "Quiet Luxury." You can choose soft linen textures, earthy sage greens, or warm taupes—the blackout technology is hidden in the weave, so you never sacrifice style for function.</p>
          </div>
        </div>
      </section>

      {/* --- Section 5: Dynamic Navigation --- */}
      <footer className="bg-slate-900 text-white p-12 rounded-[3rem] text-center">
        <h2 className="text-3xl font-bold mb-6">Explore Our Blackout Ready Ranges</h2>
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          <Link to="/roller-blinds" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full transition">Roller Blinds</Link>
          <Link to="/roman-blinds" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full transition">Roman Blinds</Link>
          <Link to="/perfect-fit-blinds" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full transition">Perfect Fit</Link>
          <Link to="/bedroom-blinds" className="bg-white/10 hover:bg-white/20 px-6 py-2 rounded-full transition">Bedroom Blinds</Link>
        </div>
        <button className="bg-blue-600 text-white px-12 py-5 rounded-full text-xl font-bold shadow-2xl hover:scale-105 transition-transform">
          Order Your Free Swatches
        </button>
      </footer>
    </div>
  );
}

export default BlackoutBlinds;